import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { CarouselType, ParsedPromo } from '../types';
import { useDispatch, useSelector } from 'react-redux';
import { productTabName } from '../../../../state/promotions';
import { setPromotionTabs } from '../../../../state/promotions/reducers';
import { useAuth } from 'react-oidc-context';
import { Carousel } from '../../../Games/Carousel';
import { PromotionCard } from '../PromotionCard';
import { useTranslations } from '../../../../hooks/useTranslationsHelper';
import { joinStrings } from '../../../../utils/string';
import { Heading } from '../../../shared/Heading';
import { Paragraph } from '../../../shared/Paragraph';
import { useDeviceInfo } from '../../../../hooks/useDeviceInfo';
import { formatCurrency } from '../../../../utils/wallets';
import { useUserBalance } from '../../../../hooks/useUserBalance';
import { PromotionClaim } from '../PromotionClaimPrompt';
import { usePromoLoggedOut } from '../../../../hooks/usePromoLoggedOut';
import './styles';
import { NavigationTemplate } from '../../../../layouts/NavigationTemplate';
import { SEO } from '../../../seo/SEO';
import { Spinner } from '../../../shared/Spinner';
import { PromotionError } from '../PromotionError';
import { ApolloError } from '@apollo/client';
import { Promotions } from '../../../Navigation/types';
import { ProductType, PromotionTabsType } from './types';

type PromotionsBlockProps = {
  isActivePromotions?: boolean;
  isUsualPromotions?: boolean;
  className?: string;
  promotionsFiltered: ParsedPromo[];
  isPromosLoading: boolean;
  promotionsLoadingError: ApolloError | unknown;
};

export const PromotionsBlock: FunctionComponent<PromotionsBlockProps> = ({
  className = '',
  promotionsFiltered,
  isPromosLoading,
  promotionsLoadingError,
}) => {
  const { t } = useTranslations();
  const isUserAuthenticated = useAuth()?.isAuthenticated;
  const dispatch = useDispatch();
  const currentProductTab = useSelector(productTabName);
  const isMobile = useDeviceInfo().isMobileDevice;
  const { userBalance } = useUserBalance();

  const [showClaim, setShowClaim] = useState<boolean>(false);
  const [claimedPromo, setClaimedPromo] = useState<ParsedPromo | null>(null);

  const { storedPromo } = usePromoLoggedOut(claimedPromo, showClaim);

  const isActivePromotions = currentProductTab === ProductType.REWARDS;

  const isPromotionError = promotionsLoadingError || !promotionsFiltered.length;

  const promotionsNav: Promotions = useMemo(
    () => ({
      tabs: [
        {
          id: ProductType.PROMOTIONS,
          name: ProductType.PROMOTIONS,
        },
        ...[
          ...(isUserAuthenticated
            ? [
                {
                  id: ProductType.REWARDS,
                  name: ProductType.PROMOTIONS,
                },
              ]
            : []),
        ],
      ],
    }),
    []
  );

  // Sort promotions by their order
  const promotionsSortedByOrder = promotionsFiltered.sort(
    (first, second) => Number(first.ordering) - Number(second.ordering)
  );

  // Retrieve unique category names
  const promoCategories = Array.from(
    new Set(
      promotionsSortedByOrder.reduce(
        (acc: (string | undefined | null)[], promo) => [
          ...acc,
          ...((isActivePromotions ? [promo.promoType] : promo.products) || []),
        ],
        []
      )
    )
  );

  const carouselCategories = Array.from(new Set(promotionsSortedByOrder.map((promo) => promo.carouselType)));

  const getCarouselView = (index: number): { isDouble: boolean; isWide: boolean } => {
    const carouselCategory = carouselCategories[index];
    const isDouble = !isMobile && (isActivePromotions || carouselCategory === CarouselType.DOUBLE);
    const isWide = !isMobile && (isDouble || carouselCategory === CarouselType.WIDE);

    // All carousels for Active Promotions (My Rewards) tab is a double type
    return {
      isDouble,
      isWide,
    };
  };

  const sortedPromosByCategories = promoCategories.map((promoCategory, index) => {
    const { isDouble, isWide } = getCarouselView(index);

    const groupedPromos = promotionsFiltered.filter((promo) =>
      isActivePromotions ? promo.promoType === promoCategory : promo.products?.includes(promoCategory)
    );

    // Group promotions by multidimensional array with 2 items per each array
    const groupedDoublePromos = groupedPromos
      // .filter((promo) => activePromoStatuses.includes(String(promo.status)))
      .reduce((acc, nextPromo, index) => {
        const isNextStep = groupedPromos[index + 1];

        return (
          index % 2 ? [...acc] : [...acc, isNextStep ? [nextPromo, groupedPromos[index + 1]] : [nextPromo]]
        ) as ParsedPromo[][];
      }, [] as ParsedPromo[][]);

    // Collect regular slides for published and slides by 2 rows for active promotions
    return isDouble
      ? groupedDoublePromos.map((filteredPromo) => (
          <div className="promotions-block__wide-slide promotions-block__wide-slide--double">
            {filteredPromo.map((promo) => (
              <PromotionCard
                key={promo.id}
                promo={promo}
                isActivePromotions={isActivePromotions}
                setShowPromoPrompt={setShowClaim}
                setClaimedPromo={setClaimedPromo}
                isWide
              />
            ))}
          </div>
        ))
      : groupedPromos.map((promo) => (
          <div className={joinStrings([isWide && 'promotions-block__wide-slide'])}>
            <PromotionCard
              key={promo.id}
              promo={promo}
              isActivePromotions={isActivePromotions}
              setShowPromoPrompt={setShowClaim}
              setClaimedPromo={setClaimedPromo}
              isWide={isWide}
            />
          </div>
        ));
  });

  const formatCategoryString = (category: string | null | undefined): string =>
    String(category)
      .replace(/_/g, ' ')
      .replaceAll(/\b(\w)/g, (match) => match.toUpperCase());

  useEffect(() => {
    if (isUserAuthenticated) {
      dispatch(setPromotionTabs(PromotionTabsType.ACTIVE));
    }

    return () => {
      dispatch(setPromotionTabs(PromotionTabsType.PROMOTIONS));
    };
  }, []);

  useEffect(() => {
    if (storedPromo) {
      setClaimedPromo(storedPromo);
      setShowClaim(true);
    }
  }, [storedPromo]);

  return (
    <NavigationTemplate promotions={promotionsNav}>
      <SEO
        title="Betzone™ Betting & Casino Offers - Promotions and Bonuses"
        description="Browse the Betzone betting offers and casino promotions ➜ See the daily offers and boost your experience with rewards and perks at every step"
        canonicalPath={'/promotions'}
      />
      <>
        {isPromosLoading ? (
          <Spinner className="promotions-list__spinner" />
        ) : isPromotionError ? (
          <PromotionError type={`${promotionsFiltered.length ? 'loading' : 'not-found'}`} />
        ) : (
          <>
            <div className={`promotions-block ${className}`}>
              {isActivePromotions && (
                <div className="promotions-block__amount-block">
                  <Heading level={5} noMargin>
                    {t('promotions.amount-block-title')}:
                  </Heading>
                  <Paragraph>
                    {formatCurrency(userBalance.bonusBalance, 'auto')} {t('promotions.amount-block-explanation')}
                  </Paragraph>
                </div>
              )}
              {promotionsFiltered?.length &&
                sortedPromosByCategories.map((sortedPromos, index) => {
                  const { isWide } = getCarouselView(index);
                  const visibleConfigData = isWide ? { wide: 1, desktop: 1 } : { wide: 2, desktop: 2 };
                  const carouselStep = isWide ? 1 : 2;

                  return (
                    <Carousel
                      key={promoCategories[index]}
                      title={formatCategoryString(promoCategories[index])}
                      slides={sortedPromos}
                      visibleConfig={{
                        tablet: 1,
                        mobile: 1,
                        ...visibleConfigData,
                      }}
                      showArrows={true}
                      showDots={true}
                      infinite={false}
                      step={carouselStep}
                      alignment="top"
                      contentAlignment="left"
                      zoom={false}
                      touchEnabled={true}
                      dragEnabled={true}
                      adjustSlidesOnResize={true}
                      paginateByRow={true}
                      carouselStyle="outside"
                    />
                  );
                })}
            </div>
          </>
        )}
        {showClaim && <PromotionClaim promo={claimedPromo} setShowClaim={setShowClaim} />}
      </>
    </NavigationTemplate>
  );
};
